import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle } from "lucide-react";
import ThemeToggle from "@/components/theme-toggle";

const signupMethods = [
  { id: "telegram", label: "Telegram", icon: "📱" },
  { id: "paypal", label: "PayPal", icon: "💳" },
  { id: "crypto", label: "Crypto Wallet", icon: "🔐" },
  { id: "email", label: "Email", icon: "📧" },
];

const features = [
  "Exclusive community access",
  "Real-time updates and insights",
  "Investment opportunities",
  "Live Q&A sessions",
  "Portfolio analytics",
];

export default function SignUp() {
  const [method, setMethod] = useState("email");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      toast.error("Please fill in all fields");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (!formData.agreeTerms) {
      toast.error("Please agree to the terms and conditions");
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      toast.success("Account created! Check your email to verify.");
      setFormData({ email: "", password: "", confirmPassword: "", agreeTerms: false });
      setIsSubmitting(false);
    }, 1500);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <a href="/" className="font-semibold text-lg tracking-tight hover:opacity-70 transition-opacity">
            MuskConnect Pro
          </a>
          <ThemeToggle />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          {/* Left Side - Form */}
          <motion.div
            className="flex flex-col justify-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <h1 className="text-4xl md:text-5xl font-medium tracking-tight mb-3">
                Join MuskConnect
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Be part of the innovation community. Create your account in minutes.
              </p>
            </motion.div>

            <motion.form onSubmit={handleSubmit} className="space-y-6" variants={containerVariants} initial="hidden" animate="visible">
              {/* Signup Methods */}
              <motion.div variants={itemVariants}>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-4">
                  Sign up with
                </p>
                <div className="grid grid-cols-4 gap-3">
                  {signupMethods.map((m) => (
                    <motion.button
                      key={m.id}
                      type="button"
                      onClick={() => setMethod(m.id)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`p-4 border text-center transition-all ${
                        method === m.id
                          ? "border-foreground bg-card"
                          : "border-border hover:border-foreground/50"
                      }`}
                    >
                      <div className="text-xl mb-2">{m.icon}</div>
                      <p className="text-xs font-medium">{m.label}</p>
                    </motion.button>
                  ))}
                </div>
              </motion.div>

              {/* Divider */}
              <motion.div variants={itemVariants} className="relative py-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-background text-muted-foreground">Or continue with email</span>
                </div>
              </motion.div>

              {/* Email */}
              <motion.div variants={itemVariants}>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3 block">
                  Email Address
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-0"
                />
              </motion.div>

              {/* Password */}
              <motion.div variants={itemVariants}>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3 block">
                  Password
                </label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="At least 8 characters"
                  className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-0"
                />
              </motion.div>

              {/* Confirm Password */}
              <motion.div variants={itemVariants}>
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-widest mb-3 block">
                  Confirm Password
                </label>
                <Input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-0"
                />
              </motion.div>

              {/* Terms */}
              <motion.div variants={itemVariants} className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  id="terms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  className="mt-1"
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                  I agree to the{" "}
                  <a href="#" className="text-foreground hover:underline">
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a href="#" className="text-foreground hover:underline">
                    Privacy Policy
                  </a>
                </label>
              </motion.div>

              {/* Submit Button */}
              <motion.div variants={itemVariants}>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50 font-medium py-6 h-auto"
                  >
                    {isSubmitting ? "Creating account..." : "Create Account"}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </motion.div>
              </motion.div>

              {/* Sign In Link */}
              <motion.p variants={itemVariants} className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <a href="/signin" className="text-foreground font-medium hover:underline">
                  Sign in
                </a>
              </motion.p>
            </motion.form>
          </motion.div>

          {/* Right Side - Benefits */}
          <motion.div
            className="hidden md:flex flex-col justify-center"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.h2 variants={itemVariants} className="text-3xl font-medium tracking-tight mb-8">
              Why Join Us
            </motion.h2>

            <motion.div className="space-y-6">
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  className="flex items-start gap-4"
                >
                  <CheckCircle className="h-5 w-5 text-foreground flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-foreground font-medium">{feature}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="mt-12 pt-8 border-t border-border"
            >
              <p className="text-sm text-muted-foreground mb-4">
                "MuskConnect Pro transformed how I stay connected with innovation. Incredible community and insights!"
              </p>
              <p className="text-xs font-medium text-foreground">— Alex Chen, Engineer</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
